/**
 * Minimal synchronous pub/sub.
 *
 * This is the only sanctioned channel for cross-subsystem communication. Modules
 * never import each other; they emit and listen. Handlers run synchronously in
 * subscription order so gameplay stays deterministic under a fixed timestep.
 *
 * Payload objects are owned by the emitter and are only valid for the duration of
 * the dispatch. Listeners that need to retain data must copy it — this keeps the
 * event bus allocation-free on the hot path.
 */
export class Events {
  constructor() {
    /** @type {Map<string, Function[]>} */
    this._map = new Map();
    // Depth guard: emitting from inside a handler is legal, but unbounded
    // recursion (A emits B emits A) would blow the stack silently.
    this._depth = 0;
  }

  /** Subscribe. Returns an unsubscribe thunk. */
  on(type, fn) {
    let list = this._map.get(type);
    if (!list) this._map.set(type, (list = []));
    list.push(fn);
    return () => this.off(type, fn);
  }

  /** Subscribe for a single dispatch. */
  once(type, fn) {
    const off = this.on(type, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  off(type, fn) {
    const list = this._map.get(type);
    if (!list) return;
    const i = list.indexOf(fn);
    if (i !== -1) list.splice(i, 1);
  }

  /**
   * Dispatch synchronously. `payload` is borrowed, not retained.
   * Handler exceptions are contained so one bad listener cannot halt the frame.
   */
  emit(type, payload) {
    const list = this._map.get(type);
    if (!list || list.length === 0) return;

    if (this._depth > 16) {
      console.warn(`[events] recursion depth exceeded on "${type}" — dropping`);
      return;
    }

    this._depth++;
    // Iterate a stable snapshot only when handlers mutate the list mid-dispatch,
    // which is rare; the common path walks the live array with no allocation.
    const n = list.length;
    for (let i = 0; i < n; i++) {
      const fn = list[i];
      if (fn === undefined) continue;
      try {
        fn(payload);
      } catch (err) {
        console.error(`[events] handler threw on "${type}"`, err);
      }
    }
    this._depth--;
  }

  clear() {
    this._map.clear();
    this._depth = 0;
  }
}
